import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Clock, LogOut, RefreshCw } from "lucide-react";

/**
 * HIPAA-compliant session timeout warning dialog
 * Shows when user session is about to expire due to inactivity
 */
const SessionWarningDialog = ({ open, onExtend, onLogout }) => {
  const [countdown, setCountdown] = React.useState(120); // 2 minutes

  React.useEffect(() => {
    if (!open) {
      setCountdown(120);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, onLogout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-xl">
              Session Expiring Soon
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base space-y-3">
            <p>
              Your session will expire in{" "}
              <span className="font-bold text-amber-600 dark:text-amber-400 text-lg">
                {formatTime(countdown)}
              </span>{" "}
              due to inactivity.
            </p>
            <p className="text-sm text-muted-foreground">
              For HIPAA compliance and the protection of patient data, sessions
              automatically expire after 15 minutes of inactivity.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2 sm:gap-2">
          <AlertDialogCancel
            onClick={onLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Log Out Now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onExtend}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            <RefreshCw className="h-4 w-4" />
            Stay Logged In
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SessionWarningDialog;
